import React, { useCallback, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useAddEmployee } from "../../hooks/useAddEmployee";
import FormInput from "../UI/FormInput";
import FormSelect from "../UI/FormSelect";
import { FormProvider, useFormState } from "react-hook-form";
import { useUnsavedChangesBlocker } from "../../hooks/useUnsavedChangesBlocker";
import UnsavedChangesDialog from "../UnsavedChangesDialog";
import toast from "react-hot-toast";
import InputDatePicker from "../../pages/EvaluationNew/components/InputDatePicker";
import {
  UserPlusIcon,
  UserEditIcon,
  UserIcon,
  MailIcon,
  LockIcon,
  BriefcaseIcon,
  IdCardIcon,
  MapPinIcon,
  ClockIcon,
  PhoneIcon,
  CloseIcon,
  DeleteIcon,
  InfoIcon,
} from "../../Icons";
import ChangePasswordModal from "./ChangePasswordModal";
import DeleteConfirmModal from "../../pages/Documents/components/DeleteConfirmModal";
import CoverageAreaModal from "../Profile/CoverageAreaModal";
import AvailabilityModal from "../Profile/AvailabilityModal";
import { request } from "../../utils/api";
import { formatSSN, formatPhone, formatName } from "../../pages/Application/application.validation";
import { EMPLOYEE_BTN, EMPLOYEE_FORM } from "./employeeTheme";
import EmployeePersonalExtraFields from "./EmployeePersonalExtraFields";
import useCurrentUser from "../../hooks/useCurrentUser";

const SUPPORT_EMAIL = "kornivus.infotech@gmail.com";

/** Compact “i” control — shows reactivation help on click (no layout shift). */
const InactiveStatusInfo = () => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative inline-flex" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full border transition-colors ${
          open
            ? "border-indigo-300 bg-indigo-50 text-indigo-700"
            : "border-slate-300 bg-white text-slate-500 hover:border-indigo-300 hover:text-indigo-600"
        }`}
        aria-label="Reactivation info"
        aria-expanded={open}
        title="Reactivation info"
      >
        <InfoIcon size={12} strokeWidth={2.25} />
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Reactivation requires support"
          className="absolute left-0 top-full z-30 mt-1.5 w-64 rounded-lg border border-indigo-100 bg-white p-3 shadow-lg sm:w-72"
        >
          <p className="text-xs font-semibold text-slate-800">
            Reactivation requires support
          </p>
          <p className="mt-1 text-xs leading-relaxed text-slate-600">
            This employee is inactive. To make them active again, contact{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="font-medium text-indigo-700 underline decoration-indigo-200 underline-offset-2 hover:text-indigo-800"
            >
              customer support
            </a>
            .
          </p>
        </div>
      ) : null}
    </div>
  );
};

const AddEmployeeModal = ({ onClose, onSuccess, defaultRoleName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams(); // Get ID from URL for updates
  const positionFromState = location.state?.position;
  const initialRole = defaultRoleName || positionFromState;


  const handleSuccessBase = useCallback(() => {
    if (onSuccess) {
      onSuccess();
    } else {
      navigate('/employees');
    }
  }, [onSuccess, navigate]);

  const saveAndLeavePending = useRef(false);
  const blockerRef = useRef(null);

  const { isAdmin, userId: currentUserId, isSurveyRole } = useCurrentUser();

  const onEmployeeSaved = useCallback(() => {
    if (saveAndLeavePending.current) {
      saveAndLeavePending.current = false;
      blockerRef.current?.proceed();
    } else {
      handleSuccessBase();
    }
  }, [handleSuccessBase]);

  const { form, roles, loading, error, onSubmit, submitForm, isSubmitting, isUpdate, isInactiveEmployee } = useAddEmployee(
    onEmployeeSaved,
    initialRole,
    id,
    isAdmin,
  );
  const { register, formState: { errors }, watch } = form;
  const { isDirty } = useFormState({ control: form.control });
  const blocker = useUnsavedChangesBlocker({ when: isUpdate && isDirty });
  blockerRef.current = blocker;

  useEffect(() => {
    if (error && saveAndLeavePending.current) {
      saveAndLeavePending.current = false;
    }
  }, [error]);

  const handleSaveAndLeave = () => {
    saveAndLeavePending.current = true;
    submitForm(() => {
      saveAndLeavePending.current = false;
    })();
  };

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      navigate('/employees');
    }
  }, [onClose, navigate]);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [coverageAreaModalOpen, setCoverageAreaModalOpen] = useState(false);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const roleOptions = roles.map(r => ({ value: r.id, label: r.name }));
  const statusOptions = isInactiveEmployee
    ? [{ value: "Inactive", label: "Inactive" }]
    : [
        { value: "Active", label: "Active" },
        { value: "Inactive", label: "Inactive" },
      ];

  const handleDeleteUser = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      const response = await request(`/users/${id}`, { method: "DELETE" });
      if (response?.status === 1) {
        toast.success(response?.result?.message || "User deleted successfully");
        setDeleteModalOpen(false);
        handleSuccessBase();
      } else {
        toast.error(response?.message || "Failed to delete user");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to delete user");
    } finally {
      setIsDeleting(false);
    }
  };

  // Get form values to construct user object for ChangePasswordModal
  const formValues = watch();
  const employeeRoleId = parseInt(formValues.roleId, 10);
  const isEmployeeSrvy = employeeRoleId === 12; // SRVY role - no Coverage Area for SRVY employees
  const employeeUser = isUpdate && id ? {
    id: parseInt(id),
    firstName: formValues.firstName || "",
    lastName: formValues.lastName || "",
    email: formValues.email || "",
  } : null;

  return (
    <div className={EMPLOYEE_FORM.page}>
      <div className={EMPLOYEE_FORM.shell}>
        <div className={EMPLOYEE_FORM.closeFloat}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={EMPLOYEE_BTN.close}
            aria-label="Close form"
          >
            <CloseIcon size={16} />
          </button>
        </div>
        <div className={EMPLOYEE_FORM.card}>
          <div className={EMPLOYEE_FORM.header}>
            <h1 className={EMPLOYEE_FORM.title}>
              {isUpdate ? "Update Employee" : "Add New Employee"}
            </h1>
            {isUpdate && isAdmin && !isSurveyRole && !isEmployeeSrvy && (
              <div className={EMPLOYEE_FORM.headerActions}>
                <button
                  type="button"
                  onClick={() => setCoverageAreaModalOpen(true)}
                  className={EMPLOYEE_BTN.outline}
                >
                  <MapPinIcon className="w-4 h-4" />
                  Coverage Area
                </button>
                <button
                  type="button"
                  onClick={() => setAvailabilityModalOpen(true)}
                  className={EMPLOYEE_BTN.outline}
                >
                  <ClockIcon className="w-4 h-4" />
                  Availability
                </button>
              </div>
            )}
          </div>

          {/* Added noValidate to disable browser default validation bubbles */}
          <FormProvider {...form}>
            <form onSubmit={onSubmit} className="space-y-8" noValidate autoComplete="off">
              <div className="space-y-8">
                {/* Section 1: Account & Role */}
                <section>
                  <div className={EMPLOYEE_FORM.sectionHead}>
                    <span className={EMPLOYEE_FORM.step}>1</span>
                    <h4 className={EMPLOYEE_FORM.sectionTitle}>Account Details</h4>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormInput label="First Name" name="firstName" register={register} error={errors.firstName} placeholder="Jane" icon={UserIcon} onInput={(e) => { e.target.value = formatName(e.target.value); }} required />
                    <FormInput label="Last Name" name="lastName" register={register} error={errors.lastName} placeholder="Doe" icon={UserIcon} onInput={(e) => { e.target.value = formatName(e.target.value); }} required />
                    <FormInput label="Email Address" name="email" type="text" register={register} error={errors.email} placeholder="jane.doe@company.com" icon={MailIcon} autoComplete="new-email" required />
                    {!isUpdate && (
                      <FormInput
                        label="Password"
                        name="password"
                        type="password"
                        register={register}
                        error={errors.password}
                        placeholder="••••••••"
                        icon={LockIcon}
                        autoComplete="new-password"
                        required
                      />
                    )}

                    <FormSelect
                      label="Position / Role"
                      name="roleId"
                      register={register}
                      error={errors.roleId}
                      options={roleOptions}
                      placeholder={roles.length ? "Select a Role" : "Loading roles..."}
                      icon={BriefcaseIcon}
                      required
                    />

                    <div>
                      <div className="mb-1.5 ml-1 flex items-center gap-1.5">
                        <label
                          htmlFor="status"
                          className="text-sm font-semibold text-slate-700"
                        >
                          Employment Status{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        {isInactiveEmployee ? <InactiveStatusInfo /> : null}
                      </div>
                      <FormSelect
                        label=""
                        name="status"
                        register={register}
                        error={errors.status}
                        options={statusOptions}
                        icon={IdCardIcon}
                        required
                        disabled={isInactiveEmployee}
                      />
                    </div>
                    <FormSelect
                      label="Work Mode"
                      name="workMode"
                      register={register}
                      error={errors.workMode}
                      options={[
                        { value: "WFO", label: "Office (WFO)" },
                        { value: "WFH", label: "Remote (WFH)" },
                      ]}
                      icon={BriefcaseIcon}
                      required
                    />
                  </div>
                </section>

                {/* Section 2: Personal Info */}
                <section>
                  <div className={EMPLOYEE_FORM.sectionHead}>
                    <span className={EMPLOYEE_FORM.step}>2</span>
                    <h4 className={EMPLOYEE_FORM.sectionTitle}>Personal Information</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <InputDatePicker label="Hire Date" name="hireDate" isRequired formatSeparator="/" />
                    <FormInput label="SSN" name="ssn" register={register} error={errors.ssn} placeholder="XXX-XX-XXXX" icon={IdCardIcon} maxLength={11} onInput={(e) => { e.target.value = formatSSN(e.target.value); }} />
                    <InputDatePicker label="Date of Birth" name="dateOfBirth" formatSeparator="/" />
                    <FormInput label="Mobile Number" name="mobileNumber" register={register} error={errors.mobileNumber} placeholder="(XXX)XXX-XXXX" icon={PhoneIcon} maxLength={13} onInput={(e) => { e.target.value = formatPhone(e.target.value); }} />
                    <EmployeePersonalExtraFields
                      register={register}
                      errors={errors}
                      canView={isAdmin}
                    />
                  </div>
                </section>

                {/* Section 3: Address */}
                <section>
                  <div className={EMPLOYEE_FORM.sectionHead}>
                    <span className={EMPLOYEE_FORM.step}>3</span>
                    <h4 className={EMPLOYEE_FORM.sectionTitle}>Address Details</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <FormInput label="Street Address" name="address" register={register} error={errors.address} placeholder="1234 Health Way, Suite 100" icon={MapPinIcon} />
                    </div>
                    <FormInput label="City" name="city" register={register} error={errors.city} placeholder="San Francisco" />
                    <FormInput label="State" name="state" register={register} error={errors.state} placeholder="CA" />
                    <FormInput label="Zip Code" name="zipCode" register={register} error={errors.zipCode} placeholder="94103" />
                    <FormInput label="Country" name="country" register={register} error={errors.country} placeholder="USA" />
                  </div>
                </section>
              </div>

              {/* Action Buttons */}
              <div className={EMPLOYEE_FORM.footer}>
                {/* Left Side: Change Password & Delete User (Admin Only, Update Mode Only) */}
                {isUpdate && isAdmin && (
                  <div className={EMPLOYEE_FORM.footerLeft}>
                    <button
                      type="button"
                      onClick={() => setPasswordModalOpen(true)}
                      className={EMPLOYEE_BTN.outline}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Change Password
                    </button>
                    {parseInt(id) !== parseInt(currentUserId) && (
                      <button
                        type="button"
                        onClick={() => setDeleteModalOpen(true)}
                        className={EMPLOYEE_BTN.danger}
                      >
                        <DeleteIcon className="w-4 h-4" />
                        Delete User
                      </button>
                    )}
                  </div>
                )}

                {/* Right Side Buttons */}
                <div className={EMPLOYEE_FORM.footerRight}>
                  <button
                    type="button"
                    onClick={handleClose}
                    className={EMPLOYEE_BTN.cancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className={EMPLOYEE_BTN.primary}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>{isUpdate ? "Updating..." : "Creating..."}</span>
                      </>
                    ) : (
                      <>
                        {isUpdate ? <UserEditIcon /> : <UserPlusIcon />}
                        {isUpdate ? "Save Changes" : "Create Employee"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>

        {/* Change Password Modal */}
        {isUpdate && employeeUser && (
          <ChangePasswordModal
            isOpen={passwordModalOpen}
            user={employeeUser}
            onClose={() => setPasswordModalOpen(false)}
          />
        )}

        {/* Coverage Area Modal (Admin only, hidden when current user or employee is SRVY) */}
        {isUpdate && id && !isSurveyRole && !isEmployeeSrvy && (
          <CoverageAreaModal
            isOpen={coverageAreaModalOpen}
            onClose={() => setCoverageAreaModalOpen(false)}
            onSuccess={() => setCoverageAreaModalOpen(false)}
            userId={id}
          />
        )}

        {isUpdate && id && !isSurveyRole && !isEmployeeSrvy && (
          <AvailabilityModal
            isOpen={availabilityModalOpen}
            onClose={() => setAvailabilityModalOpen(false)}
            onSuccess={() => setAvailabilityModalOpen(false)}
            userId={id}
          />
        )}

        {/* Delete User Confirmation Modal */}
        {isUpdate && id && (
          <DeleteConfirmModal
            isOpen={deleteModalOpen}
            onClose={() => !isDeleting && setDeleteModalOpen(false)}
            onConfirm={handleDeleteUser}
            title="Delete User"
            message="Are you sure you want to delete this user? This action cannot be undone."
            isLoading={isDeleting}
          />
        )}

        {isUpdate && (
          <UnsavedChangesDialog
            isOpen={blocker.state === "blocked" && isDirty}
            onDismiss={() => blocker.reset()}
            onSaveAndLeave={handleSaveAndLeave}
            onLeaveWithoutSaving={() => blocker.proceed()}
            isSaving={isSubmitting}
          />
        )}
      </div>
    </div>
  );
};
export default AddEmployeeModal;
